/**
 * FAQ tab layout — keyboard-accessible tabs with event delegation.
 * Works with Heritage native <accordion-custom> inside each panel.
 */

const FAQ_TAB_SELECTOR = '[data-faq-tab]';
const FAQ_PANEL_SELECTOR = '[data-faq-panel]';
const FAQ_LAYOUT_SELECTOR = '[data-faq-tab-layout]';

/**
 * @param {HTMLElement} layout
 * @param {string} tabId
 */
function activateFaqTab(layout, tabId) {
  const tabs = layout.querySelectorAll(FAQ_TAB_SELECTOR);
  const panels = layout.querySelectorAll(FAQ_PANEL_SELECTOR);

  tabs.forEach((tab) => {
    const isActive = tab.getAttribute('data-faq-tab') === tabId;
    tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
    tab.tabIndex = isActive ? 0 : -1;
  });

  panels.forEach((panel) => {
    const isActive = panel.getAttribute('data-faq-panel') === tabId;
    if (isActive) {
      panel.removeAttribute('hidden');
    } else {
      panel.setAttribute('hidden', '');
    }
  });
}

/**
 * @param {HTMLElement} tablist
 * @param {KeyboardEvent} event
 */
function handleTablistKeydown(tablist, event) {
  const tabs = Array.from(tablist.querySelectorAll(FAQ_TAB_SELECTOR));
  const currentIndex = tabs.findIndex((t) => t.getAttribute('aria-selected') === 'true');
  if (currentIndex < 0) return;

  let nextIndex = currentIndex;
  const isHorizontal = window.matchMedia('(max-width: 989px)').matches;

  switch (event.key) {
    case 'ArrowRight':
      if (isHorizontal) nextIndex = (currentIndex + 1) % tabs.length;
      break;
    case 'ArrowLeft':
      if (isHorizontal) nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
      break;
    case 'ArrowDown':
      if (!isHorizontal) nextIndex = (currentIndex + 1) % tabs.length;
      break;
    case 'ArrowUp':
      if (!isHorizontal) nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
      break;
    case 'Home':
      nextIndex = 0;
      break;
    case 'End':
      nextIndex = tabs.length - 1;
      break;
    default:
      return;
  }

  event.preventDefault();
  const nextTab = tabs[nextIndex];
  const layout = tablist.closest(FAQ_LAYOUT_SELECTOR);
  if (!layout || !nextTab) return;

  const tabId = nextTab.getAttribute('data-faq-tab');
  if (tabId) {
    activateFaqTab(layout, tabId);
    nextTab.focus();
  }
}

/**
 * Moves tab buttons rendered by FAQ category blocks into the tablist container.
 *
 * @param {HTMLElement} layout
 */
function mountFaqTabs(layout) {
  const tablist = layout.querySelector('[data-faq-tablist]');
  const panelsHost = layout.querySelector('[data-faq-panels]');
  if (!tablist || !panelsHost) return;

  const tabs = panelsHost.querySelectorAll(FAQ_TAB_SELECTOR);
  tabs.forEach((tab) => {
    if (tab instanceof HTMLElement && tab.parentElement !== tablist) {
      tablist.appendChild(tab);
    }
  });
}

/**
 * @param {HTMLElement} layout
 */
function initFaqTabLayout(layout) {
  mountFaqTabs(layout);

  const selectedTab = layout.querySelector(`${FAQ_TAB_SELECTOR}[aria-selected="true"]`);
  const firstTab = layout.querySelector(FAQ_TAB_SELECTOR);
  const tabToActivate = selectedTab || firstTab;

  if (tabToActivate) {
    const tabId = tabToActivate.getAttribute('data-faq-tab');
    if (tabId) activateFaqTab(layout, tabId);
  }

  const tablist = layout.querySelector('[data-faq-tablist]');
  if (tablist && !tablist.dataset.faqTabsBound) {
    tablist.dataset.faqTabsBound = 'true';
    tablist.addEventListener('keydown', (event) => {
      if (event instanceof KeyboardEvent) handleTablistKeydown(tablist, event);
    });
  }
}

function initAllFaqTabLayouts() {
  document.querySelectorAll(FAQ_LAYOUT_SELECTOR).forEach((layout) => {
    if (layout instanceof HTMLElement) initFaqTabLayout(layout);
  });
}

document.addEventListener('click', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const tab = target.closest(FAQ_TAB_SELECTOR);
  if (!tab) return;

  const layout = tab.closest(FAQ_LAYOUT_SELECTOR);
  if (!layout || !(layout instanceof HTMLElement)) return;

  const tabId = tab.getAttribute('data-faq-tab');
  if (tabId) activateFaqTab(layout, tabId);
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAllFaqTabLayouts);
} else {
  initAllFaqTabLayouts();
}

document.addEventListener('shopify:section:load', initAllFaqTabLayouts);
document.addEventListener('shopify:section:reorder', initAllFaqTabLayouts);
document.addEventListener('shopify:block:select', initAllFaqTabLayouts);
